import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { History, RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  sent: ["Envoyé", "text-[#10B981] bg-[#10B981]/15"],
  generated: ["Généré", "text-[#10B981] bg-[#10B981]/15"],
  running: ["En cours", "text-[#17A2B8] bg-[#17A2B8]/15"],
  rejected: ["Rejeté (volume)", "text-[#F5821F] bg-[#F5821F]/15"],
  timeout: ["Timeout", "text-[#F5821F] bg-[#F5821F]/15"],
  failed: ["Échec", "text-[#EF4444] bg-[#EF4444]/15"],
  skipped: ["Ignoré", "text-muted-foreground bg-secondary"],
};
const ERR = {
  report_scope_required: "Société à configurer",
  report_too_large: "Volume trop important",
  report_timeout: "Délai dépassé",
  email_send_failed: "Envoi email échoué",
  no_recipient: "Aucun destinataire",
};
const fmt = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");
const dur = (ms) => (ms == null ? "—" : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);

export default function ReportHistoryPanel() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async (reset) => {
    setLoading(true);
    const s = reset ? 0 : skip;
    try {
      const r = await api.get(`/report-runs?limit=${LIMIT}&skip=${s}`);
      setTotal(r.data.total);
      setRuns((prev) => (reset ? r.data.runs : [...prev, ...r.data.runs]));
      setSkip(s + r.data.runs.length);
    } catch { /* silencieux */ } finally { setLoading(false); }
  }, [skip]);

  useEffect(() => { load(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/report-runs/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = r.file_name || "rapport.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement du PDF impossible"); }
  };

  return (
    <div data-testid="report-history-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground flex items-center gap-2"><History className="w-4 h-4 brand-teal" /> Historique des rapports réellement exécutés ({total}).</p>
        <button onClick={() => load(true)} data-testid="history-refresh" className="flex items-center gap-1 text-xs text-[#17A2B8] hover:underline"><RefreshCw className="w-3.5 h-3.5" /> Actualiser</button>
      </div>
      <div className="border border-border rounded-md divide-y divide-border" data-testid="history-list">
        {runs.length === 0 && !loading && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Aucun rapport exécuté pour l'instant.</div>}
        {runs.map((r) => {
          const [lbl, cls] = STATUS[r.status] || [r.status, "text-muted-foreground bg-secondary"];
          return (
            <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`history-item-${r.id}`}>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded shrink-0 ${cls}`}>{lbl}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.report_name || "—"}{r.error_code ? <span className="text-[#EF4444] font-normal"> · {ERR[r.error_code] || r.error_code}</span> : ""}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {fmt(r.created_at)} · {r.company_name || "—"} · {r.format} · {r.vehicle_count ?? "—"} véh. · {dur(r.duration_ms)} · {r.source === "manual" ? "Manuel" : "Planifié"}
                </div>
              </div>
              {r.has_file && (
                <button onClick={() => downloadPdf(r)} data-testid={`history-download-${r.id}`} className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#17A2B8] hover:bg-[#17A2B8]/15 rounded px-2 py-1">
                  <FileDown className="w-4 h-4" /> Télécharger PDF
                </button>
              )}
            </div>
          );
        })}
      </div>
      {runs.length < total && (
        <button onClick={() => load(false)} disabled={loading} data-testid="history-load-more" className="mt-3 w-full text-sm text-[#17A2B8] hover:underline py-2 disabled:opacity-50">
          {loading ? "Chargement…" : "Charger plus"}
        </button>
      )}
    </div>
  );
}
